import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner
} from "reactstrap";
import registerSVG from "../../assets/images/auth/registerSVG.svg";
import {
  ReactComponent
    as RegisterSVGIcon
} from "../../assets/images/auth/registerSVG.svg";

// Formik Validation
import { useFormik } from "formik";
import * as Yup from "yup";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// action
import { apiError, registerUser } from "../../slices/thunks";

//redux
import { useDispatch, useSelector } from "react-redux";

import { Link, useLocation } from "react-router-dom";

//import images
import { PrimaryLogo } from "../../Components/Logo/PrimaryLogo";
import ReCaptcha from "../../Components/ReCaptcha";
import { DASHBOARD_USER_ROLES, singularRoleLabels } from "../../constants";
import { validateInviteCode } from "../../slices/inviteCode/thunk";
import { setTheme } from "../../slices/theme/reducer";
import { useCustomTheme } from "../../slices/theme/hooks";
import AuthVisual from "../../Components/Auth/AuthVisual";
import { DocumentTitle } from "../../Components/Common/DocumentTitle";

const validAccountTypes = [
  DASHBOARD_USER_ROLES.EMPLOYER,
  DASHBOARD_USER_ROLES.BROKER,
  DASHBOARD_USER_ROLES.GA, // Added GA role
];

const getInitialValues = (location) => {
  const queryParams = new URLSearchParams(location.search);
  const accountTypeFromUrl = queryParams.get("accountType");
  const inviteCodeFromUrl = queryParams.get("inviteCode");
  const roleFromUrl = queryParams.get("role");

  // Determine role from URL parameters
  let initialRole = "";
  if (validAccountTypes.includes(roleFromUrl)) {
    initialRole = roleFromUrl;
  } else if (validAccountTypes.includes(accountTypeFromUrl)) {
    initialRole = accountTypeFromUrl;
  }

  return {
    email: "",
    role: initialRole,
    password: "",
    confirm_password: "",
    inviteCode: inviteCodeFromUrl || "",
  };
};

const Register = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const initialValues = getInitialValues(location);

  const [initialized, setInitialized] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [validatingInvite, setValidatingInvite] = useState(false);
  const [inviteInvalid, setInviteInvalid] = useState(false);
  const [standardRegistration, setStandardRegistration] = useState(false);

  const customTheme = useCustomTheme();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: Yup.object({
      email: Yup.string().required("Please Enter Your Email"),
      role: Yup.string().required("Please Enter Your Account Type"),
      inviteCode: Yup.string().notRequired("Please Enter Your Invite Code"),
      password: Yup.string().required("Please Enter Your Password"),
      confirm_password: Yup.string().when("password", {
        is: (val) => (val && val.length > 0 ? true : false),
        then: Yup.string().oneOf(
          [Yup.ref("password")],
          "Confirm Password Isn't Match"
        ),
      }),
    }),
    onSubmit: async (values) => {
      try {
        setError(null);
        setLoading(true);
        await registerUser(values, dispatch);
      } catch (error) {
        setError(error.message);
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const checkInviteCode = async () => {
      if (!initialValues.inviteCode) return setInitialized(true);

      setValidatingInvite(true);
      try {

        const response = await dispatch(validateInviteCode({
          inviteCode: initialValues.inviteCode,
          role: initialValues.role,
        })).unwrap();


        console.log("Invite code response:", response);
        if (!response.valid) {
          throw new Error("Invalid invite code");
        }

        const invitedByInfo = response.invitedBy;

        const theme = invitedByInfo?.theme;

        if (theme && theme.id) {
          await dispatch(
            setTheme(theme)
          )
        }

        setInviteInfo(invitedByInfo);
        setInviteInvalid(false);
      } catch (error) {
        console.error("Invalid invite code:", error);
        setInviteInvalid(true);
        setError(null); // Don't show global error
      } finally {
        setValidatingInvite(false);
        setInitialized(true);
      }
    };

    checkInviteCode();
  }, [initialValues.inviteCode]);



  const { success } = useSelector((state) => ({
    success: state.Account.success,
    error: state.Account.error,
  }));

  useEffect(() => {
    dispatch(apiError(""));
  }, [dispatch]);

  // Check if role should be disabled
  const isRoleDisabled = !standardRegistration && (!!initialValues.role || !!inviteInfo);


  if (!initialized) {
    return (
      <div className="text-center my-4">
        <Spinner size="sm" color="primary" />
        <p className="mt-2">Loading...</p>
      </div>
    );
  }

  return (
    <React.Fragment>
      <ReCaptcha />
      <div className="auth-page-content">
        <Row className="w-100">
          <AuthVisual />
          <Col lg="6" md="12" className="auth-form-panel">
            <Card className="card-auth">
              <CardBody className="p-4 pb-1">
                <div className="text-center mt-2">
                  <div
                    style={{
                      width: 'max-content', alignSelf: 'center',
                      margin: '0 auto',
                      borderRadius: '0.5rem',
                      backgroundColor: customTheme?.secondaryColor
                    }}
                    className="text-center my-2 mb-4 p-3 d-flex align-items-center" >
                    <PrimaryLogo />
                  </div>
                  <h5 className="text-dark">Create New Account</h5>
                </div>
                <hr className="my-4" />

                {/* Show invite info if available */}
                {inviteInfo && (
                  <Alert color="info" className="mb-3">
                    <div>
                      <strong>Invited by:</strong> {inviteInfo.displayName || "Unknown"}
                      {/* {inviteInfo.company && (
                        <div><small>Company: {inviteInfo.company}</small></div>
                      )} */}
                      <div><small>You're registering as a {singularRoleLabels[validation.values.role] || validation.values.role}</small></div>
                    </div>
                  </Alert>
                )}

                {inviteInvalid && (
                  <Alert color="danger" className="mb-3">
                    This invite code is invalid or has expired.
                    <div className="mt-2">
                      <Button
                        color="secondary"
                        size="sm"
                        onClick={() => {
                          validation.setFieldValue("role", "");
                          validation.setFieldValue("inviteCode", "");
                          setInviteInvalid(false);
                          setInviteInfo(null);
                          setStandardRegistration(true); // switch to standard mode
                        }}
                      >
                        Back to standard registration
                      </Button>
                    </div>
                  </Alert>
                )}

                {validatingInvite ? (
                  <div className="text-center my-4">
                    <Spinner size="sm" color="primary" />
                    <p className="mt-2">Validating invite code...</p>
                  </div>
                ) :
                  inviteInvalid ? null :
                    (
                      <div className="p-2 mt-2">
                        <Form
                          onSubmit={(e) => {
                            e.preventDefault();
                            validation.handleSubmit();
                            return false;
                          }}
                          className="needs-validation"
                          action="#"
                        >
                          {success && success ? (
                            <>
                              {toast("Your Redirect To Login Page...", {
                                position: "top-right",
                                hideProgressBar: false,
                                className: "bg-success text-white",
                                progress: undefined,
                                toastId: "",
                              })}
                              <ToastContainer autoClose={2000} limit={1} />
                              <Alert color="success">
                                Register User Successfully and Your Redirect To Login
                                Page...
                              </Alert>
                            </>
                          ) : null}

                          {error && error ? (
                            <Alert color="danger">
                              <div>
                                {error
                                  ? error
                                  : "Something went wrong. Please try again later or contact our support team."}
                              </div>
                            </Alert>
                          ) : null}

                          <div className="mb-2">
                            <Label htmlFor="useremail" className="form-label">
                              Email <span className="text-danger">*</span>
                            </Label>
                            <Input
                              id="email"
                              name="email"
                              className="form-control bg-light"
                              placeholder="Enter email address"
                              type="email"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.email || ""}
                              invalid={
                                validation.touched.email && validation.errors.email
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.email && validation.errors.email ? (
                              <FormFeedback type="invalid">
                                <div>{validation.errors.email}</div>
                              </FormFeedback>
                            ) : null}
                          </div>

                          <div className="mb-2">
                            <Label htmlFor="userpassword" className="form-label">
                              Password <span className="text-danger">*</span>
                            </Label>
                            <Input
                              name="password"
                              type="password"
                              className="form-control bg-light"
                              placeholder="Enter Password"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.password || ""}
                              invalid={
                                validation.touched.password &&
                                  validation.errors.password
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.password &&
                              validation.errors.password ? (
                              <FormFeedback type="invalid">
                                <div>{validation.errors.password}</div>
                              </FormFeedback>
                            ) : null}
                          </div>

                          <div className="mb-2">
                            <Label htmlFor="confirmPassword" className="form-label">
                              Confirm Password <span className="text-danger">*</span>
                            </Label>
                            <Input
                              name="confirm_password"
                              type="password"
                              className="form-control bg-light"
                              placeholder="Confirm Password"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.confirm_password || ""}
                              invalid={
                                validation.touched.confirm_password &&
                                  validation.errors.confirm_password
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.confirm_password &&
                              validation.errors.confirm_password ? (
                              <FormFeedback type="invalid">
                                <div>{validation.errors.confirm_password}</div>
                              </FormFeedback>
                            ) : null}
                          </div>

                          {!isRoleDisabled && (
                            <div className="mb-3">
                              <Label htmlFor="role" className="form-label">
                                Account Type <span className="text-danger">*</span>
                              </Label>
                              <Input
                                name="role"
                                type="select"
                                className="form-control bg-light"
                                style={{
                                  cursor: isRoleDisabled ? "not-allowed" : "",
                                }}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                disabled={isRoleDisabled}
                                value={validation.values.role}
                                invalid={
                                  validation.touched.role && validation.errors.role
                                    ? true
                                    : false
                                }
                              >
                                <option value="" disabled>
                                  Select Account Type
                                </option>
                                <option value={DASHBOARD_USER_ROLES.EMPLOYER}>Employer</option>
                                <option value={DASHBOARD_USER_ROLES.BROKER}>Broker</option>
                                {/* GA option only shown if set from invite */}
                                {validation.values.role === DASHBOARD_USER_ROLES.GA && !standardRegistration && (
                                  <option value={DASHBOARD_USER_ROLES.GA}>GA</option>
                                )}
                              </Input>
                              {validation.touched.role && validation.errors.role ? (
                                <FormFeedback type="invalid">
                                  <div>{validation.errors.role}</div>
                                </FormFeedback>
                              ) : null}
                              {isRoleDisabled && (
                                <small className="text-muted">
                                  Account type is determined by your invitation
                                </small>
                              )}
                            </div>
                          )}


                          {/* {!standardRegistration && (
                            <div className="mb-3">
                              <Label htmlFor="inviteCode" className="form-label">
                                Invite Code <span className="text-muted">(Optional)</span>
                              </Label>
                              <Input
                                name="inviteCode"
                                type="text"
                                className="form-control bg-light"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                style={{
                                  cursor: initialValues.inviteCode ? "not-allowed" : "",
                                }}
                                disabled={initialValues.inviteCode}
                                value={validation.values.inviteCode}
                                invalid={
                                  validation.touched.inviteCode && validation.errors.inviteCode
                                    ? true
                                    : false
                                }
                              />
                              {validation.touched.inviteCode && validation.errors.inviteCode && (
                                <FormFeedback type="invalid">
                                  <div>{validation.errors.inviteCode}</div>
                                </FormFeedback>
                              )}
                            </div>
                          )} */}

                          {/* <div className="mb-3">
                            <Label htmlFor="inviteCode" className="form-label">
                              Invite Code{" "}
                              <span className="text-muted">(Optional)</span>
                            </Label>
                            <Input
                              name="inviteCode"
                              type="text"
                              className="form-control bg-light"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              style={{
                                cursor: initialValues.inviteCode ? "not-allowed" : "",
                              }}
                              disabled={initialValues.inviteCode}
                              value={validation.values.inviteCode}
                              invalid={
                                validation.touched.inviteCode &&
                                  validation.errors.inviteCode
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.inviteCode &&
                              validation.errors.inviteCode ? (
                              <FormFeedback type="invalid">
                                <div>{validation.errors.inviteCode}</div>
                              </FormFeedback>
                            ) : null}
                          </div> */}

                          {standardRegistration && (
                            <div>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="flexCheckDefault"
                                onChange={(e) => setIsChecked(e.target.checked)}
                              />
                              <label
                                className="form-check-label ms-2"
                                htmlFor="flexCheckDefault"
                              >
                                I agree to the{" "}
                                <a
                                  target="_blank"
                                  rel="noreferrer"
                                  href="https://www.baxrx.com/privacy-terms-of-use"
                                  className="text-primary"
                                >
                                  <span className="text-hover-underlined">
                                    Terms and Conditions
                                  </span>
                                </a>
                              </label>
                            </div>
                          )}


                          <div className="mt-4">
                            <Button
                              color="primary"
                              disabled={loading || (standardRegistration && !isChecked)}
                              className="btn btn-primary w-100"
                              type="submit"
                            >
                              {loading ? (
                                <Spinner size="sm" className="me-2" />
                              ) : null}
                              Sign Up
                            </Button>
                          </div>
                        </Form>
                      </div>
                    )}

                <div className="mt-4 text-center">
                  <p className="mb-0">
                    Already have an account ?{" "}
                    <Link
                      to="/login"
                      className="fw-semibold text-primary text-decoration-underline"
                    >
                      Signin
                    </Link>
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </React.Fragment>

  );
};

export default Register;